import React, { useState, useCallback } from 'react';
import { Chess } from 'chess.js';
import '../../styles/PVLinesDisplay.css';

const PVLinesDisplay = ({ 
  analysis, 
  analysisData = null,
  isAnalyzing: externalIsAnalyzing = false,
  isEngineReady = true,
  fen, 
  onMoveSelect, 
  currentPlayingMoveIndex = null,
  targetMoveIndex = null,
  showLineSelector = true,
  maxLines = 3 
}) => {
  const [selectedPVIndex, setSelectedPVIndex] = useState(0);

  // Helper function to check if analysis data matches the current FEN
  const analysisMatchesCurrentFEN = useCallback((data, currentFEN) => {
    if (!data || !currentFEN) return false;
    
    // Check if the analysis data is for this specific position
    const analysisFEN = data.fen || data.positionFEN;
    if (!analysisFEN) return false;
    
    // Compare the FENs, but ignore the move counters (last 2 fields)
    // This ensures we're comparing the actual board positions
    const currentFENBase = currentFEN.split(' ').slice(0, 4).join(' ');
    const analysisFENBase = analysisFEN.split(' ').slice(0, 4).join(' ');
    
    return currentFENBase === analysisFENBase;
  }, []);


  // Normalize analysis data to have a consistent format
  const normalizeAnalysisData = useCallback((data) => {
    if (!data) {
      return null;
    }
    
    // If the data already has lines property, use it directly
    if (data.lines && Array.isArray(data.lines)) {
      return data;
    }
    
    // If data has pvs property (from move.analysis format), convert to lines format
    if (data.pvs && Array.isArray(data.pvs)) {
      return {
        ...data,
        lines: data.pvs.map((pv, index) => ({
          move: pv.bestMove || pv.move || '',
          score: data.scores?.[index] || pv.evaluation || '0.0',
          pv: pv.moves || []
        }))
      };
    }
    
    // If data has analysis property (nested structure), extract and normalize it
    if (data.analysis) {
      return normalizeAnalysisData(data.analysis);
    }
    
    return null;
  }, []);

  // Use analysisData > analysis prop, prioritizing props over internal state
  // Validate FEN before using any of them
  let rawAnalysis = null;
  
  if (analysisData && analysisMatchesCurrentFEN(analysisData, fen)) {
    rawAnalysis = analysisData;
  } else if (analysis && analysisMatchesCurrentFEN(analysis, fen)) {
    rawAnalysis = analysis;
  }
  
  // Normalize the analysis data to ensure consistent format
  const finalAnalysis = normalizeAnalysisData(rawAnalysis);
  

  // Helper function to parse FEN and get starting move context
  const getFENMoveContext = (fenString) => {
    if (!fenString) return { activeColor: 'w', fullMoveNumber: 1 };
    
    const fenParts = fenString.split(' ');
    const activeColor = fenParts[1] || 'w'; // 'w' or 'b'
    const fullMoveNumber = parseInt(fenParts[5]) || 1;
    
    return { activeColor, fullMoveNumber };
  };

  // Helper function to get formatted best move with proper numbering
  const getFormattedBestMove = (lineData, fenString) => {
    if (!lineData || !lineData.bestMoveSAN) return '';
    
    const { activeColor, fullMoveNumber } = getFENMoveContext(fenString);
    
    // Format: "1. e4" for white, "1... e5" for black
    if (activeColor === 'w') {
      return `${fullMoveNumber}. ${lineData.bestMoveSAN}`;
    } else {
      return `${fullMoveNumber}... ${lineData.bestMoveSAN}`;
    }
  };

  // Helper function to group moves into White-Black pairs with proper numbering
  const groupMovesIntoPairs = (sanMoves, fenString) => {
    const { activeColor, fullMoveNumber } = getFENMoveContext(fenString);
    const movePairs = [];
    let currentMoveNumber = fullMoveNumber;
    let isWhiteToMove = activeColor === 'w';
    
    for (let i = 0; i < sanMoves.length; i++) {
      const move = sanMoves[i];
      
      if (isWhiteToMove) {
        // White's turn - start a new pair
        movePairs.push({
          moveNumber: currentMoveNumber,
          white: { move, index: i },
          black: null
        });
        isWhiteToMove = false;
      } else {
        // Black's turn
        if (movePairs.length > 0 && !movePairs[movePairs.length - 1].black) {
          // Complete the current pair
          movePairs[movePairs.length - 1].black = { move, index: i };
        } else {
          // This shouldn't happen in normal circumstances, but handle it
          movePairs.push({
            moveNumber: currentMoveNumber,
            white: null,
            black: { move, index: i }
          });
        }
        isWhiteToMove = true;
        currentMoveNumber++;
      }
    }
    
    return movePairs;
  };

  // Helper function to convert PV moves to SAN notation
  const convertPVToSAN = (pvMoves, startingFEN) => {
    if (!pvMoves || !Array.isArray(pvMoves) || pvMoves.length === 0) {
      return [];
    }


    try {
      const tempGame = new Chess(startingFEN);
      const sanMoves = [];

      for (const move of pvMoves) {
        try {
          const moveResult = tempGame.move(move);
          if (moveResult) {
            sanMoves.push(moveResult.san);
          } else {
            console.warn('Move failed to convert:', move);
            break;
          }
        } catch (error) {
          console.warn('Error converting move to SAN:', move, error.message);
          break;
        }
      }

      return sanMoves;
    } catch (error) {
      console.error('Error initializing Chess with FEN:', error.message);
      return [];
    }
  };

  // Helper function to get move button highlighting styles
  const getMoveHighlightStyle = (moveIndex) => {
    const colors = ['#ffd700', '#59b047', '#3066be']; // Gold, Green, Blue
    const baseColor = colors[selectedPVIndex];
    
    const isCurrentMove = currentPlayingMoveIndex === moveIndex;
    const isTargetMove = targetMoveIndex === moveIndex;
    
    if (isCurrentMove || isTargetMove) {
      return {
        backgroundColor: baseColor,
        color: '#000000', // Black text
        borderColor: baseColor,
        fontWeight: 'bold'
      };
    }
    
    return {
      borderColor: baseColor,
      cursor: 'pointer'
    };
  };

  // Handle sequential move playback
  const handleSequentialMovePlayback = useCallback((currentLine, clickedMoveIndex) => {
    if (!onMoveSelect || !currentLine.pv || currentLine.pv.length === 0) {
      return;
    }

    // Get the moves to play (from start up to clicked move, inclusive)
    const movesToPlay = currentLine.pv.slice(0, clickedMoveIndex + 1);
    
    // Convert LAN moves to SAN for better representation
    let tempGame = null;
    try {
      tempGame = new Chess(fen);
    } catch (error) {
      console.error('Error initializing chess game for sequential playback:', error.message);
      return;
    }

    const convertedMoves = [];
    
    for (let i = 0; i < movesToPlay.length; i++) {
      const lanMove = movesToPlay[i];
      
      try {
        const moveResult = tempGame.move(lanMove);
        if (moveResult) {
          convertedMoves.push({
            lan: lanMove,
            san: moveResult.san,
            from: moveResult.from,
            to: moveResult.to
          });
        } else {
          console.warn(`PVLinesDisplay: Failed to convert LAN move: ${lanMove}`);
          break;
        }
      } catch (error) {
        console.error(`PVLinesDisplay: Error converting move ${lanMove}:`, error);
        break;
      }
    }

    // Call the onMoveSelect callback with the move sequence
    if (onMoveSelect) {
      onMoveSelect({
        movesToPlay: convertedMoves,
        targetMoveIndex: clickedMoveIndex,
        lineIndex: selectedPVIndex,
        isSequentialPlayback: true
      });
    }
  }, [onMoveSelect, fen, selectedPVIndex]);

  // Switch between PV lines
  const switchToPVLine = useCallback((direction) => {
    if (!finalAnalysis?.pvs?.length) return;
    
    if (direction === 'prev' && selectedPVIndex > 0) {
      setSelectedPVIndex(selectedPVIndex - 1);
    } else if (direction === 'next' && selectedPVIndex < Math.min(finalAnalysis.pvs.length - 1, maxLines - 1)) {
      setSelectedPVIndex(selectedPVIndex + 1);
    }
  }, [selectedPVIndex, finalAnalysis?.pvs?.length, maxLines]);

  // Render a single PV line
  const renderPVLine = (lineIndex) => {
    // Only render PV lines when we have valid analysis data - no individual "Calculating..." states
    if (!finalAnalysis?.pvs?.length || lineIndex >= finalAnalysis.pvs.length) {
      return null; // Don't render anything if no data
    }

    const colors = ['#ffd700', '#59b047', '#3066be'];
    const currentLine = finalAnalysis.pvs[lineIndex];
    const sanMoves = convertPVToSAN(currentLine.pv || [], fen);
    
    // Only render if we have valid SAN moves - no individual "Calculating..." states
    if (sanMoves.length === 0) {
      return null; // Don't render anything if no valid moves
    }

    const movePairs = groupMovesIntoPairs(sanMoves, fen);

    return (
      <div key={lineIndex} className="pv-line" style={{ borderColor: colors[lineIndex] }}>
        <div className="pv-line-header">
          <h4 style={{ color: colors[lineIndex] }}>
            {lineIndex === 0 ? 'Top Engine Line' : lineIndex === 1 ? '2nd Best Line' : '3rd Best Line'}
          </h4>
          <div className="pv-line-info">
            <span className="pv-depth">Depth={analysis?.depth || '?'}</span>
            <span className="pv-score" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
              {currentLine.normalizedCP ? (currentLine.normalizedCP > 0 ? '+' : '') + currentLine.normalizedCP : 'N/A'}
            </span>
          </div>
        </div>
        
        <div className="pv-moves-container">
          {movePairs.map((pair, pairIndex) => (
            <div key={pairIndex} className="move-pair">
              <span className="move-number">
                {pair.moveNumber}.
              </span>
              
              {pair.white && (
                <button 
                  className="pv-move-btn white-move"
                  style={getMoveHighlightStyle(pair.white.index)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSequentialMovePlayback(currentLine, pair.white.index);
                  }}
                  title={`Click to play moves 1-${pair.white.index + 1} in sequence (${pair.white.move})`}
                >
                  {pair.white.move}
                </button>
              )}
              
              {pair.black && (
                <button 
                  className="pv-move-btn black-move"
                  style={getMoveHighlightStyle(pair.black.index)}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSequentialMovePlayback(currentLine, pair.black.index);
                  }}
                  title={`Click to play moves 1-${pair.black.index + 1} in sequence (${pair.black.move})`}
                >
                  {pair.black.move}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Main render - check engine ready state and analyzing state
  if (!isEngineReady) {
    return (
      <div className="pv-lines-display">
        <div className="pv-lines-header">
          <h3>Engine Suggestions</h3>
        </div>
        <div className="pv-lines-content analyzing-load">
          Waking up Stockfish...&nbsp;&nbsp; <i className="fa-solid fa-chess-knight fa-bounce"></i>
        </div>
      </div>
    );
  }
  
  if (externalIsAnalyzing || !finalAnalysis || !finalAnalysis.pvs || finalAnalysis.pvs.length === 0) {
    return (
      <div className="pv-lines-display">
      <div className="pv-lines-header" style={{width: '100%', textAlign: 'center'}}>
          <h3 style={{textAlign: 'center', width: '100%'}}>Engine Suggested Puzzle Solution</h3>
        </div>
        <div className="pv-lines-content analyzing-load">
          Calculating...&nbsp;&nbsp; <i className="fa-solid fa-chess-knight fa-bounce"></i>
        </div>
      </div>
    );
  }

  const linesToShow = Math.min(finalAnalysis.pvs.length, maxLines);

  return (
    <div className="pv-lines-display">
      <div className="pv-lines-header" style={{width: '100%', textAlign: 'center'}}>
        <h3 style={{textAlign: 'center', width: '100%'}}>Engine Suggested Puzzle Solution</h3>
        {showLineSelector && linesToShow > 1 && (
          <div className="pv-line-selector">
            <i
              onClick={() => switchToPVLine('prev')} 
              disabled={selectedPVIndex === 0} 
              title="Previous line"
              className="fa-solid fa-square-minus" 
              style={{ 
                fontSize: '20px', 
                color: selectedPVIndex === 0 ? '#666' : 'gray', 
                cursor: selectedPVIndex === 0 ? 'not-allowed' : 'pointer' 
              }}
            ></i>

            <span style={{ margin: '0 10px', color: '#fff' }}>
              {selectedPVIndex + 1} of {linesToShow}
            </span>

            <i 
              onClick={() => switchToPVLine('next')} 
              disabled={selectedPVIndex >= linesToShow - 1} 
              title="Next line"
              className="fa-solid fa-square-plus" 
              style={{ 
                fontSize: '20px', 
                color: selectedPVIndex >= linesToShow - 1 ? '#666' : 'gray', 
                cursor: selectedPVIndex >= linesToShow - 1 ? 'not-allowed' : 'pointer' 
              }}
            ></i>
          </div>
        )}
      </div>
      
      <div className="pv-lines-content">
        {!isEngineReady ? (
          // Show "Waking up Stockfish..." when engine not ready
          <div className="analyzing-load">
            Waking up Stockfish...&nbsp;&nbsp; <i className="fa-solid fa-chess-knight fa-bounce"></i>
          </div>
        ) : externalIsAnalyzing ? (
          // Show single "Calculating..." when analyzing
          <div className="analyzing-load">
            Calculating...&nbsp;&nbsp; <i className="fa-solid fa-chess-knight fa-bounce"></i>
          </div>
        ) : showLineSelector ? (
          // Single line mode with navigation (only when analysis is complete)
          renderPVLine(selectedPVIndex)
        ) : (
          // Show all lines mode (only when analysis is complete)
          <div className="pv-all-lines">
            {Array.from({ length: linesToShow }, (_, index) => renderPVLine(index))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PVLinesDisplay;